import angular from 'angular';
import sanitize from 'angular-sanitize';
import bootstrap from 'angular-ui-bootstrap';

import i18n from '../i18n';
import tourService from './tour-service.js';
import tourContentDirective from './tour-content-directive.js';

/**
 * @ngdoc module
 * @name akamai.components.tour
 *
 * @requires module:ngSanitize
 * @requires ui.bootstrap (for ui.bootstrap.position),
 * @requires module:akamai.components.i18n
 *
 * @guideline https://design.akamai.com/components/tour/
 *
 * @description Tour is an instructional popup adjacent to any predetermined element.
 * It is a multistep workflow that serves the purpose of providing educational info on different
 * sections of the page. Users can move forward and backwards during the tour.
 * User can cancel the tour at anytime and request not to show the tour from that page.
 *
 * @example index.js
 * function MyController(tour) {
 *
 *   tour.start({
 *     onDisplay = function(activeStep) {...},
 *     onRemove = function(toRemove) {...},
 *     title: 'components.tour.header',
 *     titleValues: {name: 'Gadget'},
 *     hideBackdrop: false, //true|false
 *     steps: [
 *       {
 *         title: 'components.tour.header',
*          titleValues: {name: 'Text 1'},
 *         content: 'intructional text',
 *         selector: "div.tour-1"
 *         placement: 'top'//"top|right|bottom|left"
 *       },
 *       {
 *         title: 'components.tour.header',
 *         titleValues: {name: 'Text 2'},
 *         content: 'intructional text',
 *         selector: "div.tour-2",
 *         placement: 'bottom'//"top|right|bottom|left"
 *       }
 *     ]
 *   });
 * }
 */
export default angular.module('akamai.components.tour', [
  sanitize,
  bootstrap,
  i18n.name
])

/**
 * @ngdoc service
 * @name akamai.components.tour.service:tour
 *
 * @description
 * A service that opens multiple tour popups in a predetermined sequence.
 * It provides forward and backward steps and constructs the TourController for
 * template to display proper values
 *
 * APIs(properties) for Tour object:
 * @param {Function} [onDisplay] Callback function to pass current display step info object
 * @param {Function} [onRemove] Callback function to pass true or false value,
 *    true: to remove tour from the page, false: to not to remove.
 * @param {String|TranslateKey}[title='Tour'] String value (key) to be displayed or translated
 *    in the tour popup header.
 *    If string is a translation key, it will automatically be translated.
 * @param {object} [titleValues={name:'xx'}] A hash for passing in for translation value
 *    support with 'name' variable.
 * @param {boolean} [hideBackdrop=false] A flag to turn on/off the backdrop rendering
 * @param {Object[]} steps A list of steps; each step contains infomation needed
 *     to be displayed on the tour tip popup
 *     @param {String|TranslateKey}[title='Tour'] String value (key) to be displayed or translated
 *      in the tourtip popup header.
 *     @param {object} [titleValues={name:'xx'}] a hash for passing in for translation value
 *      support with 'name' variable.
 *     @param {String} content Text content to be displayed in the tour popup body (required)
 *     @param {String} seletor Valid selector for locating DOM element from current page (required)
 *     @param {String} [placement='bottom'] The tour popup position,
 *      valid options: top|right|bottom|left
 *
 * __NOTE__:
 * 1. Tour object is required to pass in tour data from tour.start, otherwise error will be thrown
 * 2. Step content property and value are required, otherwise it will be skipped to the next step
 * 3. Valid selector is required, otherwise that step will be skipped
 * 4. Steps are displayed in sequence based on the order they appear in the step array
 * 5. Cookie to remember the steps is not implemented.
 */
.service('tour', tourService)

/**
 * @private
 * @name akamai.components.wizard.directive:akamTourContent
 * @description
 * A simple directive that injects step content templates into the body
 */
.directive('akamTourContent', tourContentDirective);
